import { FC } from 'react';

import { PunchupTile, PunchupStatTileGrid } from 'features/common/components/Punchup/primitives/display/PunchupTile';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { TicketTypeSummary } from 'features/PunchupPages/CompedTickets/SendCompedTicketsModal/types';
import { formatPwywRange } from 'utils/utils';

import styles from './TicketAvailabilityTiles.module.css';

// Announced when a tile is over-reached (requested qty > available). Surfaced BOTH as visually-hidden tile
// text and appended to the help tooltip, so the state never depends on the red ring's colour alone (WCAG 1.4.1).
const OVER_REACH_MESSAGE = 'Requested quantity exceeds availability.';

// PWYW range uses the shared formatPwywRange (single source, identical wording to the
// Show-Dashboard tile: "from $X up to $Y"). Fixed price / FREE keep their existing display.
const formatTicketPrice = (tt: TicketTypeSummary): string => {
	if (tt.payWhatYouWant) {
		return formatPwywRange(tt.minPrice, tt.maxPrice) ?? 'Pay what you want';
	}
	if (!tt.initialPrice) return 'FREE';
	return `$${tt.initialPrice.toFixed(2)}`;
};

export interface TicketAvailabilityTilesProps {
	ticketTypes: TicketTypeSummary[];
	/**
	 * In bulk-upload mode, the set of ticket type IDs whose aggregate requested
	 * quantity across all rows exceeds available inventory. Renders a red ring on
	 * the affected tile so the venue manager can see the conflict at a glance.
	 * Omit (or pass an empty set) outside of bulk mode.
	 */
	exceededTypeIds?: Set<number>;
}

export const TicketAvailabilityTiles: FC<TicketAvailabilityTilesProps> = ({ ticketTypes, exceededTypeIds }) => {
	// Tile colour rail — the ticket type's colour code (resolved by name from the shared source),
	// the SAME colour the tier shows on its table tag and pool member chip.
	const { resolveByName } = useTicketTypeColors();

	if (ticketTypes.length === 0) return null;

	return (
		<PunchupStatTileGrid>
			{ticketTypes.map((tt) => {
				// The tier's OWN remaining (amount − sold). Pools and tiers are shown as SEPARATE entities: the
				// pool clamp (min with poolRemaining) is applied only to the stepper max + the on-send gate, never
				// to this display — so a pooled tier reads its own inventory here, the Pools tile reads the pool's,
				// and the stepper enforces the combined min(ticket_left, pool_left) under the hood.
				const available = tt.amount - tt.sold;
				const isExceeded = exceededTypeIds?.has(tt.iD) ?? false;
				return (
					<div
						key={tt.iD}
						className={isExceeded ? styles.exceededWrapper : undefined}
						data-exceeded={isExceeded || undefined}
					>
						{/* Non-colour signal for the over-reach state (the red ring is reinforcement only) — a
						    visually-hidden line a screen reader announces. WCAG 1.4.1. */}
						{isExceeded && <span className={styles.srOnly}>{OVER_REACH_MESSAGE}</span>}
						<PunchupTile
							title={tt.name}
							singleLineTitle
							compact
							railColor={resolveByName(tt.name)}
							value={String(available)}
							descriptions={[formatTicketPrice(tt)]}
							singleLineDescriptions
							helpButton={
								<PunchupHelpButton
									mainText={`${tt.name} (${formatTicketPrice(tt)}): ${tt.sold} of ${
										tt.amount
									} sold, ${available} available.${isExceeded ? ` ${OVER_REACH_MESSAGE}` : ''}`}
								/>
							}
						/>
					</div>
				);
			})}
		</PunchupStatTileGrid>
	);
};
